import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Training from "@/pages/Training";
import AuthCallback from "@/pages/AuthCallback";
import GoogleSignupComplete from "@/pages/GoogleSignupComplete";
import SharePage from "@/pages/SharePage";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Refund from "@/pages/Refund";
import Contact from "@/pages/Contact";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import BankDashboard from "@/pages/bank/BankDashboard";
import FieldDashboard from "@/pages/field/FieldDashboard";
import OfficeDashboard from "@/pages/office/OfficeDashboard";
import ManagerDashboard from "@/pages/manager/ManagerDashboard";

function ProtectedRoute({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to={`/login/${role || "admin"}`} replace />;
  if (role && user.role !== role) return <Navigate to={`/${user.role}`} replace />;
  return children;
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="bottom-right" richColors />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/training" element={<Training />} />
            <Route path="/login/:role" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/signup/:role" element={<Signup />} />
            <Route path="/signup/google/:role" element={<GoogleSignupComplete />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/share/:token" element={<SharePage />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refund" element={<Refund />} />
            <Route path="/contact" element={<Contact />} />
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute role="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bank/*"
              element={
                <ProtectedRoute role="bank">
                  <BankDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/field/*"
              element={
                <ProtectedRoute role="field">
                  <FieldDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/office/*"
              element={
                <ProtectedRoute role="office">
                  <OfficeDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manager/*"
              element={
                <ProtectedRoute role="manager">
                  <ManagerDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
